import type { KeyboardEvent } from 'react';
import React from 'react';
import type { BadgeProps } from 'react-bootstrap';
import { Badge, useClassNameMapper } from 'react-bootstrap';
import { SharedComponent } from '../../../enums';
import Icons from '../../../icons';
import useTranslation from '../../useTranslation';
import Button from '../Button/Button';
import { ButtonVariant } from '../Button/enums';
import { IconSize } from '../Icon/enums';
import IconRenderer from '../Icon/Icon';
import localStyles from './Chip.module.pcss';
import { KeyBoardEvents } from '@aurora/shared-types/community/enums';

interface ChildComponentProps {
  /**
   * Class name(s) to apply to the child component element.
   */
  className: string;
}

interface Props<ItemType> extends BadgeProps {
  /**
   * Type of the chip.
   */
  type: ItemType;

  /**
   * Text or styled react component as chip's main content.
   */
  Content: string | React.FC<React.PropsWithChildren<ChildComponentProps>>;

  /**
   * Icon or image of the chip.
   */
  Icon?: React.FC<React.PropsWithChildren<ChildComponentProps>>;

  /**
   * When set to true, uses avatar rules for border radius
   */
  hasAvatar?: boolean;

  /**
   * When set to true, highlights the chip.
   */
  hasFocus?: boolean;

  /**
   * Class to be applied to the chip
   */
  className?: string;

  /**
   * Callback when a chip is removed.
   *
   * @callback
   * @param id - the id of the chip removed
   */
  onRemove?: (item: ItemType) => void;

  /**
   * Class to be applied to the user chip text
   */
  contentClassName?: string;
}

/**
 * @author Shalin Amin, Willi Hyde
 */
const Chip = <ItemType extends object>({
  type,
  className,
  Content,
  Icon,
  pill = false,
  hasAvatar,
  hasFocus,
  onRemove,
  contentClassName
}: Props<ItemType>): React.ReactElement => {
  const cx = useClassNameMapper(localStyles);
  const { formatMessage, loading: textLoading } = useTranslation(SharedComponent.CHIP);

  if (textLoading) {
    return null;
  }

  /**
   * Handle Keyboard Enter/Space events to remove this chip
   * @param event React KeyboardEvent
   */
  function handleKeyUp(event: KeyboardEvent) {
    if (event.key === KeyBoardEvents.ENTER || event.key === KeyBoardEvents.SPACE) {
      onRemove(type);
    }
  }

  return (
    <Badge
      data-testid="Chip"
      pill={pill}
      variant="light"
      className={cx(className, 'lia-chip', { 'lia-has-focus': hasFocus })}
    >
      {Icon && (
        <Icon
          className={cx('lia-icon', { 'lia-is-pill': pill }, { 'lia-has-visual-style': hasAvatar })}
        />
      )}
      <Content className={cx(contentClassName, 'lia-text')} />
      {onRemove && (
        <Button
          variant={ButtonVariant.LINK}
          aria-label={formatMessage('remove')}
          className={cx('lia-remove')}
          data-testid="Chip.CloseIcon"
          onMouseUp={(): void => onRemove(type)}
          onTouchEnd={(): void => onRemove(type)}
          onKeyUp={handleKeyUp}
        >
          <IconRenderer icon={Icons.CloseIcon} size={IconSize.PX_12} className={cx('lia-close')} />
        </Button>
      )}
    </Badge>
  );
};

export default Chip;
